import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from "react";

import styles from "./NotificationQuickForm.module.css";
import i18n from "../../../Services/i18n";
import { useToasts } from "../../../LayoutComponents/ToastContext";
import LoadingSpinner from "../../../LayoutComponents/LoadingSpinner";
import UserServiceService from "../../../Services/userServiceService";

const NotificationQuickForm = forwardRef(({ horse, initialValue = null }, ref) => {
  const { addToast } = useToasts();

  const [services, setServices] = useState([]);
  const [isLoadingServices, setIsLoadingServices] = useState(true);

  const [userServiceExternalId, setUserServiceExternalId] = useState(
    initialValue?.userServiceExternalId ?? "",
  );

  const [daysSinceLastService, setDaysSinceLastService] = useState(
    initialValue?.daysSinceLastService ?? 30,
  );

  const [message, setMessage] = useState(initialValue?.message ?? "");

  const [endOn, setEndOn] = useState(
    initialValue?.endOn ? new Date(initialValue.endOn).toISOString().substring(0, 10) : "",
  );

  const initialState = useMemo(
    () => ({
      userServiceExternalId: initialValue?.userServiceExternalId ?? "",
      daysSinceLastService: initialValue?.daysSinceLastService ?? 30,
      message: initialValue?.message ?? "",
      endOn: initialValue?.endOn ? new Date(initialValue.endOn).toISOString().substring(0, 10) : "",
    }),
    [initialValue],
  );

  useEffect(() => {
    let alive = true;

    const loadServices = async () => {
      try {
        setIsLoadingServices(true);
        console.log("CCRD");
        const result = await UserServiceService.GetUserServiceList(false);

        if (!alive) return;

        const horseServices = (result ?? [])
          .filter((service) => service.isForHorses)
          .sort((a, b) => (a.cardName ?? "").localeCompare(b.cardName ?? ""));

        setServices(horseServices);
      } catch (ex) {
        if (!alive) return;

        addToast(i18n.t("Generic.ServerError"), "BACKENDERROR", "ERROR", true);
      } finally {
        if (alive) {
          setIsLoadingServices(false);
        }
      }
    };

    loadServices();

    return () => {
      alive = false;
    };
  }, [addToast]);

  const validate = () => {
    const days = Number(daysSinceLastService);

    if (!Number.isInteger(days) || days <= 0) {
      addToast(
        i18n.t("NotificationForm.Validation.InvalidDays"),
        "NOTIFICATION_INVALID_DAYS",
        "ERROR",
        true,
      );

      return false;
    }

    if (!message.trim()) {
      addToast(
        i18n.t("NotificationForm.Validation.MessageRequired"),
        "NOTIFICATION_MESSAGE_REQUIRED",
        "ERROR",
        true,
      );

      return false;
    }

    if (message.trim().length > 1000) {
      addToast(
        i18n.t("NotificationForm.Validation.MessageTooLong"),
        "NOTIFICATION_MESSAGE_TOO_LONG",
        "ERROR",
        true,
      );

      return false;
    }

    return true;
  };

  const getFormData = () => {
    return {
      ...(initialValue?.externalId ? { externalId: initialValue.externalId } : {}),

      clientExternalId: null,
      horseExternalId: horse.externalId,

      userServiceExternalId: userServiceExternalId || null,

      daysSinceLastService: Number(daysSinceLastService),

      notificationType: 1,

      message: message.trim(),

      // La notification reste valide jusqu'à la fin
      // de la journée sélectionnée.
      endOn: endOn ? `${endOn}T23:59:59` : null,
    };
  };

  const isDirty = () => {
    return (
      userServiceExternalId !== initialState.userServiceExternalId ||
      Number(daysSinceLastService) !== Number(initialState.daysSinceLastService) ||
      message !== initialState.message ||
      endOn !== initialState.endOn
    );
  };

  useImperativeHandle(ref, () => ({
    validate,
    getFormData,
    isDirty,
  }));

  return (
    <div className={styles.container}>
      <div className={styles.target}>
        <span className={styles.targetLabel}>{i18n.t("NotificationForm.Horse")}</span>

        <span className={styles.targetValue}>{horse.cardName ?? horse.name}</span>
      </div>

      <div className={styles.field}>
        <label htmlFor="notificationService">{i18n.t("NotificationForm.Service")}</label>

        {isLoadingServices ? (
          <div className={styles.loadingServices}>
            <LoadingSpinner />
          </div>
        ) : (
          <select
            id="notificationService"
            value={userServiceExternalId}
            onChange={(e) => setUserServiceExternalId(e.target.value)}>
            <option value="">{i18n.t("NotificationForm.AllServices")}</option>

            {services.map((service) => (
              <option key={service.externalId} value={service.externalId}>
                {service.cardName}
              </option>
            ))}
          </select>
        )}

        <small className={styles.help}>
          {userServiceExternalId
            ? i18n.t("NotificationForm.ServiceSpecificHelp")
            : i18n.t("NotificationForm.AllServicesHelp")}
        </small>
      </div>

      <div className={styles.inlineFields}>
        <div className={styles.field}>
          <label htmlFor="notificationDays">{i18n.t("NotificationForm.Days")}</label>

          <div className={styles.daysRow}>
            <input
              id="notificationDays"
              type="number"
              min="1"
              step="1"
              value={daysSinceLastService}
              onChange={(e) => setDaysSinceLastService(e.target.value)}
            />

            <span>{i18n.t("NotificationForm.DaysSuffix")}</span>
          </div>

          <small className={styles.help}>{i18n.t("NotificationForm.DaysHelp")}</small>
        </div>

        <div className={styles.field}>
          <label htmlFor="notificationEndOn">{i18n.t("NotificationForm.EndOn")}</label>

          <input
            id="notificationEndOn"
            type="date"
            value={endOn}
            onChange={(e) => setEndOn(e.target.value)}
          />

          <small className={styles.help}>{i18n.t("NotificationForm.EndOnHelp")}</small>
        </div>
      </div>

      <div className={styles.field}>
        <label htmlFor="notificationMessage">{i18n.t("NotificationForm.Message")}</label>

        <textarea
          id="notificationMessage"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          maxLength={1000}
          rows={5}
          placeholder={i18n.t("NotificationForm.MessagePlaceholder")}
        />

        <div className={styles.characterCount}>{message.length} / 1000</div>
      </div>
    </div>
  );
});

export default NotificationQuickForm;
